import React, { useState } from "react";

export interface PageNavigatorProps {
  /**
   * Total number of pages (X)
   */
  totalPages: number;
  /**
   * Currently selected page (P)
   */
  currentPage: number;
  /**
   * Callback when a page is selected
   */
  onPageChange: (page: number) => void;
}

export const PageNavigator: React.FC<PageNavigatorProps> = ({
  totalPages,
  currentPage,
  onPageChange,
}) => {
  const getPagesToShow = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    const firstPage = 1;
    const lastPage = totalPages;

    // Always include the first page
    pages.push(firstPage);

    // Add "..." if there's a gap between the first page and P-2
    if (currentPage - 2 > firstPage + 1) {
      pages.push("...");
    }

    // Add P-2, P-1, P, P+1, P+2 (if within bounds)
    for (let i = currentPage - 2; i <= currentPage + 2; i++) {
      if (i > firstPage && i < lastPage) {
        pages.push(i);
      }
    }

    // Add "..." if there's a gap between P+2 and the last page
    if (currentPage + 2 < lastPage - 1) {
      pages.push("...");
    }

    // Always include the last page
    if (lastPage !== firstPage) {
      pages.push(lastPage);
    }

    return pages;
  };

  const pagesToShow = getPagesToShow();

  return (
    <div className="flex flex-row space-x-2 items-center justify-center">
      {pagesToShow.map((page, index) => {
        if (page == "...") {
          return (
            <span
              key={index}
              className="font-archb text-silver ~text-xs/sm ~px-0.5/1"
            >
              ...
            </span>
          );
        }
        return (
          <button
            key={index}
            className={`font-archb ~text-sm/base cursor-pointer ~rounded-md/lg ~px-1.5/2 ~py-0.5/1 ${currentPage === page ? "bg-gray" : "bg-silver"}`}
            onClick={
              page == "..." ? undefined : () => onPageChange(page as number)
            }
          >
            {page}
          </button>
        );
      })}
    </div>
  );
};

// Example usage
const App: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = 20;

  return (
    <div style={{ padding: "20px" }}>
      <h1>Page Navigator</h1>
      <PageNavigator
        totalPages={totalPages}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
      />
      <p>Current Page: {currentPage}</p>
    </div>
  );
};

export default App;
